/**
 * Chapitre 1 — « Diagnostic de la ville ».
 *
 * Quatre gestes : explorer (découvrir des problèmes), classer (les ranger dans
 * une catégorie), analyser (un objet connecté peut-il aider ?), décider (choisir
 * trois priorités). Puis un score, calculé selon `chapitres[0].score`.
 */
import type { ConnectedUsefulness, Content } from './content-schema.js';
import type { ActionResult, GameState } from './state.js';
import { refus, withAction } from './state.js';

/** Nombre maximum de priorités au podium (cf. plan, étape « Décider »). */

/**
 * Un problème est « aligné sur les habitants » quand sa `priorite_habitants`
 * vaut 2 ou 3 — c'est la définition donnée par le plan.
 */
export const PRIORITE_HABITANTS_ALIGNEE = [2, 3];

/**
 * Nombre de secondes chances au classement.
 *
 * Le plan en prévoit une par problème, et laisse le chiffre ouvert (« Points de
 * décision à trancher par l'enseignant »). Une seconde chance = deux corrections
 * au total pour un même problème.
 */

/* ------------------------------------------------------------------ */
/* Étape 1 — Explorer                                                  */
/* ------------------------------------------------------------------ */

/** L'îlot repère un problème dans un quartier. Découvrir deux fois est sans effet. */
export function discoverProblem(
  state: GameState,
  content: Content,
  problemId: string,
): ActionResult {
  if (!content.problemes.some((p) => p.id === problemId)) {
    return refus('probleme_inconnu', `Problème absent du contenu : « ${problemId} ».`);
  }
  if (state.discoveredProblems.includes(problemId)) {
    return { ok: true, state };
  }

  return {
    ok: true,
    state: withAction(
      { ...state, discoveredProblems: [...state.discoveredProblems, problemId] },
      { type: 'decouvrir_probleme', problemId },
    ),
  };
}

/* ------------------------------------------------------------------ */
/* Étape 2 — Classer                                                   */
/* ------------------------------------------------------------------ */

/**
 * L'îlot range un problème découvert dans une catégorie.
 * Reclasser le même problème écrase le choix précédent : c'est la « seconde
 * chance » prévue par le plan, qui est gérée par l'interface.
 */
export function classifyProblem(
  state: GameState,
  content: Content,
  problemId: string,
  categoryId: string,
): ActionResult {
  if (!content.problemes.some((p) => p.id === problemId)) {
    return refus('probleme_inconnu', `Problème absent du contenu : « ${problemId} ».`);
  }
  if (!state.discoveredProblems.includes(problemId)) {
    return refus(
      'probleme_non_decouvert',
      `Le problème « ${problemId} » n'a pas encore été repéré : il ne peut pas être classé.`,
    );
  }
  if (!content.categories_problemes.some((c) => c.id === categoryId)) {
    return refus('categorie_inconnue', `Catégorie absente du contenu : « ${categoryId} ».`);
  }

  return {
    ok: true,
    state: withAction(
      { ...state, classification: { ...state.classification, [problemId]: categoryId } },
      { type: 'classer_probleme', problemId, categoryId },
    ),
  };
}

/* ------------------------------------------------------------------ */
/* Étape 3 — Analyser                                                  */
/* ------------------------------------------------------------------ */

/** L'îlot répond « oui / en partie / non » à « un objet connecté peut-il aider ? ». */
export function analyzeProblem(
  state: GameState,
  content: Content,
  problemId: string,
  reponse: ConnectedUsefulness,
): ActionResult {
  if (!content.problemes.some((p) => p.id === problemId)) {
    return refus('probleme_inconnu', `Problème absent du contenu : « ${problemId} ».`);
  }
  if (!state.discoveredProblems.includes(problemId)) {
    return refus(
      'probleme_non_decouvert',
      `Le problème « ${problemId} » n'a pas encore été repéré : il ne peut pas être analysé.`,
    );
  }

  return {
    ok: true,
    state: withAction(
      { ...state, connectedAnalysis: { ...state.connectedAnalysis, [problemId]: reponse } },
      { type: 'analyser_probleme', problemId, reponse },
    ),
  };
}

/* ------------------------------------------------------------------ */
/* Étape 4 — Décider                                                   */
/* ------------------------------------------------------------------ */

/** L'îlot pose son podium : trois problèmes au plus, dans l'ordre. */
export function setPriorities(
  state: GameState,
  content: Content,
  priorities: readonly string[],
): ActionResult {
  const places = content.reglages.places_au_podium;
  if (priorities.length > places) {
    return refus(
      'trop_de_priorites',
      `Le podium compte ${places} places au maximum, ${priorities.length} problèmes ont été proposés.`,
    );
  }
  if (new Set(priorities).size !== priorities.length) {
    return refus(
      'priorite_en_double',
      'Un même problème ne peut pas occuper deux places du podium.',
    );
  }

  for (const problemId of priorities) {
    if (!content.problemes.some((p) => p.id === problemId)) {
      return refus('probleme_inconnu', `Problème absent du contenu : « ${problemId} ».`);
    }
    if (!state.discoveredProblems.includes(problemId)) {
      return refus(
        'probleme_non_decouvert',
        `Le problème « ${problemId} » n'a pas encore été repéré : il ne peut pas être une priorité.`,
      );
    }
  }

  return {
    ok: true,
    state: withAction(
      { ...state, priorities: [...priorities] },
      { type: 'definir_priorites', priorities: [...priorities] },
    ),
  };
}

/* ------------------------------------------------------------------ */
/* Score                                                               */
/* ------------------------------------------------------------------ */

export interface Chapter1ScoreDetail {
  readonly bonnesCategories: number;
  readonly bonnesAnalyses: number;
  readonly prioritesAlignees: number;
  readonly total: number;
}

/**
 * Calcule le score du chapitre 1 selon le barème de `chapitres[0].score` :
 * un problème bien classé, un problème bien analysé, et une priorité que les
 * habitants jugent importante (`priorite_habitants` à 2 ou 3).
 *
 * Seuls les problèmes effectivement découverts comptent.
 */
export function scoreChapter1(state: GameState, content: Content): Chapter1ScoreDetail {
  const chapitre = content.chapitres.find((c) => c.id === 'ch1');
  const bareme = chapitre?.score;
  if (!bareme) {
    throw new Error('Le chapitre 1 ne déclare pas de barème de score.');
  }

  const problemesParId = new Map(content.problemes.map((p) => [p.id, p]));

  let bonnesCategories = 0;
  let bonnesAnalyses = 0;

  for (const problemId of state.discoveredProblems) {
    const probleme = problemesParId.get(problemId);
    if (!probleme) {
      continue;
    }
    if (state.classification[problemId] === probleme.categorie) {
      bonnesCategories += 1;
    }
    if (state.connectedAnalysis[problemId] === probleme.objet_connecte_utile) {
      bonnesAnalyses += 1;
    }
  }

  let prioritesAlignees = 0;
  for (const problemId of state.priorities) {
    const probleme = problemesParId.get(problemId);
    if (probleme && PRIORITE_HABITANTS_ALIGNEE.includes(probleme.priorite_habitants)) {
      prioritesAlignees += 1;
    }
  }

  const total =
    bonnesCategories * bareme.bonne_categorie +
    bonnesAnalyses * bareme.bonne_analyse_connecte +
    prioritesAlignees * bareme.priorite_alignee_habitants;

  return { bonnesCategories, bonnesAnalyses, prioritesAlignees, total };
}

/** Enregistre le score du chapitre 1 dans l'état. */
export function recordChapter1Score(state: GameState, content: Content): GameState {
  const { total } = scoreChapter1(state, content);
  return { ...state, score: { ...state.score, ch1: total } };
}

/* ------------------------------------------------------------------ */
/* Correction du classement                                            */
/* ------------------------------------------------------------------ */

export interface ClassificationVerdict {
  readonly problemId: string;
  /** La catégorie choisie par l'îlot, ou `null` s'il n'a rien rangé. */
  readonly choisie: string | null;
  /** La catégorie du contenu. */
  readonly attendue: string;
  readonly correcte: boolean;
  /** L'îlot peut-il encore corriger ce problème ? */
  readonly peutReessayer: boolean;
}

export interface ClassificationReview {
  readonly state: GameState;
  readonly verdicts: readonly ClassificationVerdict[];
  /** Nombre de problèmes bien rangés, pour le compteur affiché. */
  readonly corrects: number;
}

/**
 * Corrige le classement de l'îlot.
 *
 * La correction n'apparaît qu'ici, jamais pendant le glisser-déposer : l'idée
 * est que le groupe discute avant de valider. Chaque problème encore faux peut
 * être repris tant que le nombre de corrections reste sous
 * `reglages.secondes_chances + 1`.
 */
export function reviewClassification(state: GameState, content: Content): ClassificationReview {
  const problemesParId = new Map(content.problemes.map((p) => [p.id, p]));
  const tentatives = { ...state.classificationAttempts };
  const verdicts: ClassificationVerdict[] = [];

  for (const problemId of state.discoveredProblems) {
    const probleme = problemesParId.get(problemId);
    if (!probleme) {
      continue;
    }

    const choisie = state.classification[problemId] ?? null;
    const correcte = choisie === probleme.categorie;

    // Chaque passage par la correction compte, juste ou faux : c'est ce qui
    // permet de distinguer « pas encore corrigé » de « corrigé et juste ».
    const faites = (tentatives[problemId] ?? 0) + 1;
    tentatives[problemId] = faites;

    verdicts.push({
      problemId,
      choisie,
      attendue: probleme.categorie,
      correcte,
      peutReessayer: !correcte && faites <= content.reglages.secondes_chances,
    });
  }

  const corrects = verdicts.filter((v) => v.correcte).length;

  return {
    state: withAction(
      { ...state, classificationAttempts: tentatives },
      {
        type: 'valider_classement',
        corrects: verdicts.filter((v) => v.correcte).map((v) => v.problemId),
      },
    ),
    verdicts,
    corrects,
  };
}

/** L'îlot a-t-il rangé tous les problèmes qu'il a repérés ? */
export function classementComplet(state: GameState): boolean {
  return (
    state.discoveredProblems.length > 0 &&
    state.discoveredProblems.every((problemId) => state.classification[problemId] !== undefined)
  );
}
